import Header from '../components/Header';

const GarageTerms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              ParkRez Garage Partner Terms and Conditions
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Partnership Agreement</h2>
              <p className="text-gray-700 mb-4">
                By becoming a ParkRez Garage Partner, you agree to provide parking services to ParkRez users in accordance with these terms and conditions. This agreement establishes a partnership between your parking facility and ParkRez for the purpose of facilitating parking reservations and transactions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Requirements</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>2.1 Parking Space Availability:</strong> You must maintain accurate availability of parking spaces and promptly update your listing when spaces become unavailable.</p>
                <p><strong>2.2 Service Quality:</strong> You agree to provide parking services that meet reasonable quality standards and maintain a safe, clean parking environment.</p>
                <p><strong>2.3 Operating Hours:</strong> You must accurately represent your operating hours and ensure parking is available during stated hours.</p>
                <p><strong>2.4 Pricing:</strong> You agree to honor the pricing listed on ParkRez and provide transparent pricing without hidden fees.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Payment and Revenue Sharing</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>3.1 Revenue Split:</strong> ParkRez will retain a percentage of each transaction as a service fee. The specific percentage will be communicated during the onboarding process.</p>
                <p><strong>3.2 Payment Schedule:</strong> Payments will be processed and distributed according to the schedule outlined in your partner agreement.</p>
                <p><strong>3.3 Tax Responsibilities:</strong> You are responsible for reporting and paying all applicable taxes on revenue received through ParkRez.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Experience and Support</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>4.1 Customer Service:</strong> You agree to provide reasonable customer service to ParkRez users who park at your facility.</p>
                <p><strong>4.2 Dispute Resolution:</strong> In the event of disputes with users, you agree to work with ParkRez to resolve issues fairly and promptly.</p>
                <p><strong>4.3 User Safety:</strong> You are responsible for maintaining a safe parking environment and addressing any safety concerns promptly.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data and Privacy</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>5.1 User Data:</strong> You agree to protect user data and use it only for the purpose of providing parking services.</p>
                <p><strong>5.2 Privacy Compliance:</strong> You must comply with all applicable privacy laws and regulations.</p>
                <p><strong>5.3 Data Sharing:</strong> You agree to share necessary data with ParkRez for service provision and analytics.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Termination</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>6.1 Termination by Partner:</strong> You may terminate this agreement with 30 days written notice to ParkRez.</p>
                <p><strong>6.2 Termination by ParkRez:</strong> ParkRez may terminate this agreement immediately for violations of these terms or for other reasonable cause.</p>
                <p><strong>6.3 Post-Termination:</strong> Upon termination, you agree to honor existing reservations and complete any outstanding obligations.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Liability and Insurance</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>7.1 Insurance Requirements:</strong> You must maintain appropriate insurance coverage for your parking facility and operations.</p>
                <p><strong>7.2 Liability Limits:</strong> ParkRez's liability is limited to the amount of fees paid to ParkRez in the 12 months preceding any claim.</p>
                <p><strong>7.3 Indemnification:</strong> You agree to indemnify ParkRez against claims arising from your parking operations.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Compliance and Legal</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>8.1 Legal Compliance:</strong> You must comply with all applicable laws, regulations, and permits related to parking operations.</p>
                <p><strong>8.2 Zoning and Permits:</strong> You are responsible for ensuring your parking facility complies with local zoning laws and has necessary permits.</p>
                <p><strong>8.3 Accessibility:</strong> You must comply with accessibility requirements and provide reasonable accommodations when possible.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Communication and Updates</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>9.1 Communication:</strong> You agree to maintain open communication with ParkRez regarding your parking operations.</p>
                <p><strong>9.2 Updates:</strong> ParkRez may update these terms with reasonable notice. Continued use of the platform constitutes acceptance of updated terms.</p>
                <p><strong>9.3 Notifications:</strong> You agree to receive communications from ParkRez regarding service updates, policy changes, and operational matters.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
              <div className="space-y-4 text-gray-700">
                <p>For questions about these terms or your partnership with ParkRez, please contact us:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Email: partners@parkrez.com</li>
                  <li>Phone: (555) 123-4567</li>
                  <li>Address: 123 Parking Street, City, State 12345</li>
                </ul>
              </div>
            </section>

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                By agreeing to these terms, you acknowledge that you have read, understood, and agree to be bound by all provisions of this agreement. This agreement constitutes the entire understanding between you and ParkRez regarding your partnership.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GarageTerms;
